// Push/pull for the `tenth_steps` table. Shape mirrors checkInsSync — see
// that file for the design notes on date-string conversion and LWW.

import { format } from 'date-fns';
import { z } from 'zod';
import { db, withDeleteHookSuppressed, type TenthStep } from '../db';
import { isSupabaseConfigured, supabase } from '../supabase/client';
import { cursorOrEpoch, readCursor, writeCursor } from './cursor';
import { dedupeTenthStepByContent } from './dedupe';
import type { SyncResult } from './types';
import { EMPTY_SYNC_RESULT } from './types';

// See checkInsSync.ts for the rationale: parse-at-the-boundary so a server
// schema drift fails loud here instead of corrupting Dexie.
const remoteTenthStepRowSchema = z.object({
  id: z.string(),
  client_id: z.string(),
  owner_id: z.string(),
  entry_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  wrongs: z.array(z.string()).nullable(),
  amends: z.array(z.string()).nullable(),
  is_completed: z.boolean(),
  notes: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
});

type RemoteTenthStepRow = z.infer<typeof remoteTenthStepRowSchema>;

interface PushPayload {
  client_id: string;
  owner_id: string;
  entry_date: string;
  wrongs: string[];
  amends: string[];
  is_completed: boolean;
  notes: string;
  updated_at: string;
  deleted_at: string | null;
}

export function buildTenthStepPushPayload(
  row: TenthStep,
  ownerId: string,
  now: Date = new Date(),
): PushPayload {
  return {
    client_id: row.clientId ?? crypto.randomUUID(),
    owner_id: ownerId,
    entry_date: format(row.date, 'yyyy-MM-dd'),
    wrongs: row.wrongs,
    amends: row.amends,
    is_completed: row.isCompleted,
    notes: row.notes,
    updated_at: (row.updatedAt ?? now).toISOString(),
    deleted_at: row.deletedAt ? row.deletedAt.toISOString() : null,
  };
}

export function applyRemoteTenthStep(
  remote: RemoteTenthStepRow,
): Omit<TenthStep, 'id'> {
  const [y, m, d] = remote.entry_date.split('-').map(Number);
  const localDate =
    Number.isFinite(y) && Number.isFinite(m) && Number.isFinite(d)
      ? new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1, 12, 0, 0)
      : new Date(remote.entry_date);
  return {
    clientId: remote.client_id,
    date: localDate,
    wrongs: remote.wrongs ?? [],
    amends: remote.amends ?? [],
    isCompleted: remote.is_completed,
    notes: remote.notes ?? '',
    updatedAt: new Date(remote.updated_at),
    deletedAt: remote.deleted_at ? new Date(remote.deleted_at) : null,
  };
}

export async function syncTenthStepsUp(userId: string): Promise<SyncResult> {
  if (!isSupabaseConfigured() || !supabase) return { ...EMPTY_SYNC_RESULT };
  const client = supabase;
  const result: SyncResult = { ...EMPTY_SYNC_RESULT, errors: [] };

  const local = await db.tenthSteps.toArray();
  const withClientIds = local.filter((r) => r.clientId);
  if (withClientIds.length > 0) {
    const payload = withClientIds.map((r) =>
      buildTenthStepPushPayload(r, userId),
    );
    const { error } = await client
      .from('tenth_steps')
      .upsert(payload, { onConflict: 'owner_id,client_id' });
    if (error) {
      result.errors.push(`tenth_steps push: ${error.message}`);
    } else {
      result.pushed += payload.length;
    }
  }

  const tombstones = await db.tombstones
    .where('table')
    .equals('tenthSteps')
    .toArray();
  if (tombstones.length > 0) {
    const payload = tombstones.map((t) => ({
      client_id: t.clientId,
      owner_id: userId,
      entry_date: format(t.deletedAt, 'yyyy-MM-dd'),
      updated_at: t.deletedAt.toISOString(),
      deleted_at: t.deletedAt.toISOString(),
    }));
    const { error } = await client
      .from('tenth_steps')
      .upsert(payload, { onConflict: 'owner_id,client_id' });
    if (error) {
      result.errors.push(`tenth_steps tombstone push: ${error.message}`);
    } else {
      result.pushed += payload.length;
      const ids = tombstones
        .map((t) => t.id)
        .filter((id): id is number => id !== undefined);
      if (ids.length > 0) await db.tombstones.bulkDelete(ids);
    }
  }
  return result;
}

export async function syncTenthStepsDown(userId: string): Promise<SyncResult> {
  if (!isSupabaseConfigured() || !supabase) return { ...EMPTY_SYNC_RESULT };
  const client = supabase;
  const result: SyncResult = { ...EMPTY_SYNC_RESULT, errors: [] };

  // See checkInsSync for the full rationale: dedupe runs only on the first
  // sync per device and only for live remote rows, to avoid mis-cascading
  // a server tombstone onto a fresh local creation.
  const isFirstSync = readCursor('tenth_steps') === null;

  const since = cursorOrEpoch('tenth_steps');
  const { data, error } = await client
    .from('tenth_steps')
    .select('*')
    .eq('owner_id', userId)
    .gt('updated_at', since)
    .order('updated_at', { ascending: true });
  if (error) {
    result.errors.push(`tenth_steps pull: ${error.message}`);
    return result;
  }

  const parsed = z.array(remoteTenthStepRowSchema).safeParse(data);
  if (!parsed.success) {
    result.errors.push(
      `tenth_steps pull: row schema mismatch — ${parsed.error.message}`,
    );
    return result;
  }
  const rows = parsed.data;
  if (rows.length === 0) return result;

  const incomingClientIds: ReadonlySet<string> = new Set(
    rows.map((r) => r.client_id),
  );

  const pendingTombstoneClientIds: ReadonlySet<string> = new Set(
    (await db.tombstones.where('table').equals('tenthSteps').toArray()).map(
      (t) => t.clientId,
    ),
  );

  await withDeleteHookSuppressed(async () => {
    await db.transaction('rw', db.tenthSteps, async () => {
      for (const remote of rows) {
        if (pendingTombstoneClientIds.has(remote.client_id)) {
          continue;
        }
        if (isFirstSync && !remote.deleted_at) {
          await dedupeTenthStepByContent(
            remote.client_id,
            remote.entry_date,
            incomingClientIds,
          );
        }

        const local = await db.tenthSteps
          .where('clientId')
          .equals(remote.client_id)
          .first();

        if (remote.deleted_at) {
          if (local?.id != null) await db.tenthSteps.delete(local.id);
          result.pulled += 1;
          continue;
        }

        const incomingUpdated = new Date(remote.updated_at).getTime();
        const localUpdated = local?.updatedAt?.getTime() ?? -Infinity;
        if (local && localUpdated >= incomingUpdated) {
          result.conflicts += 1;
          continue;
        }
        const next = applyRemoteTenthStep(remote);
        if (local?.id != null) {
          await db.tenthSteps.put({ ...next, id: local.id });
        } else {
          await db.tenthSteps.add(next);
        }
        result.pulled += 1;
      }
    });
  });

  const last = rows[rows.length - 1];
  if (last) writeCursor('tenth_steps', last.updated_at);

  return result;
}
